// Fleet Reports — one metric's page (2026-07-14).
// Reusable across Fuel Efficiency / Fuel Consumption / Driving Score: weekly
// per-vehicle bars + fleet headline on top, daily lines below (when the metric
// has a daily grain), then the data table with RAG-graded cells — the same
// weekly-then-daily order as the xlsx sheets it replaces.
import React from "react";
import { Card, Grid } from "@mui/material";
import FleetBar from "./charts/FleetBar";
import FleetLine from "./charts/FleetLine";
import StatTile from "./StatTile";
import { RAG, ragForSeries, ragBg } from "./ragColors";

const VEHICLE_COLORS = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

const fmt = (v, d = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? "No data" : Number(v).toFixed(d);

const sectionTitle = (text) => (
  <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
    {text}
  </p>
);

export default function MetricSection({ metric, title, unit, better, decimals = 2, hasDaily, report, agg = "total" }) {
  const m = (report && report.metrics && report.metrics[metric]) || null;
  // The window figure is a SUM for additive metrics (consumption) but an AVERAGE
  // for rate/score metrics (efficiency L/100km, driving score) — label it honestly.
  const aggCol = agg === "average" ? "Average" : "Total";
  const aggTitle = agg === "average" ? "Averages" : "Totals";
  const reverse = better === "higher";
  const weekRows = (m && m.perVehicleWeek) || [];
  // Memoized: ragForSeries returns a NEW closure each call, and an unmemoized one
  // in the barData deps would defeat that useMemo and rebuild the chart on every render.
  const ragWeek = React.useMemo(() => ragForSeries(weekRows, reverse), [weekRows, reverse]);

  // MEMOIZE the chart props. FleetBar/FleetLine key their useLayoutEffect on
  // [data]/[days, series]; a fresh array identity on every render would dispose
  // and rebuild the whole am5.Root each time the parent re-renders (which the
  // hoisted RTK query does several times per fetch), making the charts flicker
  // and churn WebGL contexts. Rebuild only when the payload actually changes.
  const days = React.useMemo(() => ((m && m.fleetDay) || []).map((d) => d.date), [m]);

  const barData = React.useMemo(
    () =>
      weekRows.map((r) => ({
        key: r.sensorId, // identity — names are not guaranteed unique
        label: r.name || r.sensorId,
        value: r.value,
        color: ragWeek(r.value),
      })),
    [weekRows, ragWeek]
  );

  // Per-vehicle daily series. Keyed by SENSORID, not by display name: two
  // vehicles can legitimately share a name (nothing in the licence, the config
  // cache or the devices collection enforces uniqueness), and keying the amCharts
  // valueYField by name would silently collapse them into one line.
  const lineSeries = React.useMemo(() => {
    if (!hasDaily || !m) return [];
    return ((report && report.vehicles) || []).map((v, i) => ({
      key: v.sensorId,
      label: v.name || v.sensorId,
      color: VEHICLE_COLORS[i % VEHICLE_COLORS.length],
      points: (m.perVehicleDay || [])
        .filter((r) => r.sensorId === v.sensorId)
        .reduce((acc, r) => {
          acc[r.date] = r.value;
          return acc;
        }, {}),
    }));
  }, [hasDaily, m, report]);

  if (!m) return null;
  const vehicles = (report && report.vehicles) || [];

  return (
    <div>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={4} md={3}>
          <StatTile label={`Fleet ${title}`} value={m.fleetWeek} unit={unit} better={better} decimals={decimals} />
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle(`Per vehicle (${unit})`)}
            <FleetBar data={barData} unit={unit} height="240px" />
          </Card>
        </Grid>
      </Grid>

      {hasDaily ? (
        <Card style={{ padding: "16px", marginBottom: "20px" }}>
          {sectionTitle(`Daily per vehicle (${unit})`)}
          <FleetLine days={days} series={lineSeries} unit={unit} height="300px" />
        </Card>
      ) : null}

      <Card style={{ padding: "16px" }}>
        {sectionTitle(`${aggTitle} (${unit})`)}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#e2e2e2" }}>
                <th style={{ textAlign: "left", padding: "10px" }}>Vehicle</th>
                <th style={{ textAlign: "right", padding: "10px" }}>{aggCol} ({unit})</th>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((r) => {
                const c = ragWeek(r.value);
                return (
                  <tr key={r.sensorId} style={{ borderBottom: "1px solid #eeeeee" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{r.name}</td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        backgroundColor: ragBg(c),
                        color: r.value === null ? RAG.neutral : "#333333",
                      }}
                    >
                      {fmt(r.value, decimals)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
                <td style={{ padding: "10px" }}>Fleet</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{fmt(m.fleetWeek, decimals)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "13px", color: "grey", marginTop: "12px", marginBottom: 0 }}>
          <b>Hint:</b>{" "}
          {metric === "consumption"
            ? "The total is measured across the whole selected period, not by adding the daily figures - fuel burned between the last reading of one day and the first of the next belongs to the period."
            : `The fleet figure is the average of the ${vehicles.length} vehicle averages, so every vehicle counts equally. Vehicles with no data are left out, never counted as zero.`}
        </p>
      </Card>
    </div>
  );
}
